'use client';
import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import './styles.scss';
import Skeleton from '@/components/atoms/skeletons';
import { ContextualIndicator } from '@/interfaces/home';
import { useIndicatorSession } from './hooks/useIndicatorSession';
import IndicatorList from './components/IndicatorList';
import ChartDisplay from './components/ChartDisplay';
import { IndicatorSessionProps } from './types';
import { AreaVariant, slugMapping } from '../mapsPackage';
import { mapModifiers, processSlug } from '@/utils/funtion';
import ChartPackageSkeleton from '@/components/molecules/skeleton-component/chartPacakge';
import Empty from '@/components/atoms/empty';

const IndicatorSession: React.FC<IndicatorSessionProps> = ({
  hasMap,
  area,
  slug,
  screen,
  onLoadingChange,
  slugData,
  slugTitle,
}) => {
  const {
    tags,
    isLoading,
    activeId,
    setActiveId,
    searchData,
    type,
    setType,
    initialData,
    chartData,
    handleGetChardData,
    handleGetChartDataByRange,
    handleInputChange,
    handleSelectTime,
    dataSource,
  } = useIndicatorSession(slug, screen, slugData);
  const listRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<HTMLDivElement>(null);
  const [viewActive, setViewActive] = useState<boolean>(true);

  React.useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);
  const [localSlug, setLocalSlug] = useState<AreaVariant>('VN');

  useEffect(() => {
    const newSlug = processSlug(area || '');
    setLocalSlug(slugMapping[newSlug] as AreaVariant);
  }, [area]);

  const filteredIndicators = useMemo(() => {
    if (!searchData.trim()) return dataSource || [];
    const exactMatch = searchData.match(/^["'](.*?)["']$/);
    return (
      dataSource?.filter((item: ContextualIndicator) => {
        const name =
          item?.indicator?.name ||
          item?.localIndicator?.name ||
          item?.industryIndicator?.name;
        if (!name) return false;
        if (exactMatch) {
          return name.toLowerCase().includes(exactMatch[1].toLowerCase());
        } else {
          const searchWords = searchData.toLowerCase().split(/\s+/);
          return searchWords.every((word) => name.toLowerCase().includes(word));
        }
      }) || []
    );
  }, [searchData, dataSource]);

  useEffect(() => {
    chartRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, []);

  return (
    <div className="indicator-session" id={slug}>
      {!filteredIndicators?.length && !chartData ? (
        <div className="indicator-session-chart_empty">
        <Empty />
      </div>
      ) :(
        isLoading ? (
          <Skeleton active type="default" loading paragraph={{ rows: 12 }} />
        ) : (
          <React.Fragment>
            <div className="indicator-session-title">
              {slugTitle || tags?.title}
            </div>
            <div className="line"></div>
            <div className="indicator-session-content">
              <div
                className={mapModifiers(
                  'indicator-session-content_wrapper',
                  !viewActive ? 'chart' : 'list',
                )}
              >
                <div
                  ref={listRef}
                  className={`indicator-session-list ${viewActive ? '' : 'list_active'}`}
                >
                  <IndicatorList
                    searchData={searchData}
                    handleInputChange={handleInputChange}
                    filteredIndicators={filteredIndicators}
                    activeId={activeId}
                    setActiveId={setActiveId}
                    setType={setType}
                    handleGetChardData={handleGetChardData}
                    slug={slug}
                    area={localSlug ? localSlug : 'VN'}
                  />
                </div>
                <div
                  ref={chartRef}
                  className={`indicator-session-chart ${viewActive ? 'chart_active' : ''}`}
                >
                  <Suspense fallback={<ChartPackageSkeleton />}>
                    <ChartDisplay
                      hasMap={hasMap}
                      area={localSlug ? localSlug : 'VN'}
                      chartData={chartData}
                      initialData={initialData}
                      type={type}
                      handleSelectTime={handleSelectTime}
                      handleGetChartDataByRange={handleGetChartDataByRange}
                    />
                  </Suspense>
                </div>
              </div>
            </div>
          </React.Fragment>
        )
      )}
      
    </div>
  );
};

export default IndicatorSession;
